import React, { Component } from 'react';
import classNames from 'classnames';
import Cell from './cell';
import Styles from './index.less';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import throttle from 'lodash/throttle';
import { isSupportNativeTitleBar } from '@src/js/manager/native-titlebar-manager';
import { MOBILE_HEAD_HEIGHT, MOBILE_NATIVE_JUDGE_HEIGHT } from '@src/js/constant';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';

export const LIST_VISIBLE_POSITION = {
    TOP: 'top',
    CENTER: 'center',
    BOTTOM: 'bottom',
};

export type LIST_VISIBLE_POSITION = typeof LIST_VISIBLE_POSITION[keyof typeof LIST_VISIBLE_POSITION];

// 这个 id 是给是另一个代码库中 snapshot 服务使用的，是获取当前列表的大小和位置来帮助截图，一定不可以修改或删除，否则会导致生成截图尺寸错误
// snapshot 仓库中是使用 document.querySelector('#screenshot-element-for-snapshot-server') 来获取这个元素的
const listContainerId = 'screenshot-element-for-snapshot-server';

interface ListProps {
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    layout: string;
    canScroll?: boolean;
    editMode: boolean;

    cellCountForList?: (list: List) => number;
    cellForListAtIndex?: (index: number) => React.ReactNode;
    cellIDForListAtIndex?: (index: number) => string;
    onScroll?: (list: List) => void;
    onCellAppearAtIndex?: (cell: HTMLElement, index: number) => void;
    onCellDisappearAtIndex?: (cell: HTMLElement, index: number) => void;
    onVisibleCellsChange?: (visibleCellIDs: string[]) => void;
}

interface ListState {
    frameWidth: string;
    frameHeight: string;
}

export default class List extends Component<ListProps, ListState> {
    public static Cell = Cell;

    public static Layout = {
        HORIZONTAL: 'horizontal',
        VERTICAL: 'vertical',
    };

    private visibleCellIDs: string[] = [];
    private cellIDs: string[] = [];
    private container: HTMLDivElement | null;
    private listContainer: HTMLDivElement | null;
    private operationBtnContainerRef: any;
    private clientHeight: number;
    private fixedScrollTop: number;

    public constructor(props: ListProps) {
        super(props);

        this.state = {
            frameWidth: '100%',
            frameHeight: '100%',
        };

        this.registerScrollSlideIntoViewByIndexMobile();
    }

    public componentDidMount() {
        this.renderFrameAndCheckDisplay();
    }

    public componentWillUnmount() {
        window.removeEventListener('scrollSlideIntoViewByIndexMobile', this.scrollSlideIntoView);
    }

    public componentDidUpdate() {
        this.renderFrameAndCheckDisplay();
    }

    public setClientHeight = (clientHeight: number): void => {
        this.clientHeight = clientHeight;
    };

    public scrollChange = (fixedScrollTop: number): void => {
        // window.log.debug(`scrollChange: ${fixedScrollTop}`);
        const event = new Event('scroll', { bubbles: true });
        if (this.container) {
            this.container.dispatchEvent(event);
            fixedScrollTop && (this.fixedScrollTop = fixedScrollTop);
        } else {
            window.dispatchEvent(event);
        }
    };

    public render(): React.ReactNode {
        const listClass = classNames({
            [Styles.list]: true,
            [Styles[this.props.layout]]: true,
            [this.props.className as string]: true,
        });
        const vertical = this.isVertical(); // 是否纵向显示
        const containerStyle: React.CSSProperties = {
            display: 'flex',
            flexDirection: vertical ? 'column' : 'row',
            position: 'absolute',
            // 解决编辑状态下列表被客户端头部遮挡问题，pad中编辑状态下，没有客户端头部
            paddingTop: this.getPaddingTop(vertical),
        };

        return <div
            id={this.props.id}
            ref={(el) => {
                this.container = el;
            }}
            className={listClass}
            onScroll={this.handleSlideListScroll.bind(this)}
        >
            <div
                style={containerStyle}
                ref={(el) => {
                    this.listContainer = el;
                }}
                id={listContainerId}
            >
                { this.renderChildren() }
            </div>
        </div>;
    }

    public getVisibleCellIndexes = () => this.visibleCellIDs.map(id => this.cellIDs.indexOf(id));

    public getPageNum = () => {
        const scrollView = this.container!;
        const scale = window.mobileZoomScale === 1 ? 1 : window.mobileZoomScale;
        const listRect = scrollView?.getBoundingClientRect();
        this.clientHeight && (listRect.height = this.clientHeight);
        const container = this.getListContainer();
        const rect = container?.getBoundingClientRect();

        // 缩放后列表整体高度乘以缩放倍数再进行判断
        const frameHeight =  rect.height * scale;
        const headerHeight = (MOBILE_HEAD_HEIGHT + this.topBannerHeight) * (window.mobileZoomScale - 1);
        const listBottom = listRect.bottom;
        const topOffset = this.fixedScrollTop || scrollView.scrollTop;

        let pageNum = this.cellIDs.indexOf(this.visibleCellIDs[0]);
        // 列表拉至最底部时，页码直接取最大值显示
        if ((frameHeight  <= listBottom + topOffset + headerHeight + 4) && (frameHeight > listBottom)) {
            pageNum = this.cellIDs.length - 1;
        }
        return pageNum;
    };

    /**
     * iframe缩放的时候，隐藏TAB底部栏组件
     * @param {string | number} zoomScale 缩放比例
     */
    public handleAfterMobileZoom = (zoomScale: number) => {
        const opacity = Number(zoomScale) === 1 ? 1 : 0;
        if (this.operationBtnContainerRef?.style) {
            this.operationBtnContainerRef.style.opacity = opacity;
        }
    };

    public scrollToCellAtIndex = (index: number, position: string) => {
        requestAnimationFrame(() => {
            const listContainer = this.getListContainer();
            const scrollView = this.container!;
            const cellDom = listContainer?.childNodes[index] as HTMLElement;
            if (cellDom) {
                if (this.props.layout === List.Layout.HORIZONTAL) {
                    switch (position) {
                        case LIST_VISIBLE_POSITION.TOP:
                        {
                            scrollView.scrollLeft = cellDom.offsetLeft;
                            break;
                        }
                        case LIST_VISIBLE_POSITION.CENTER:
                        {
                            scrollView.scrollLeft = cellDom.offsetLeft
                                + ((cellDom.clientWidth - scrollView.clientWidth) / 2);
                            break;
                        }
                        case LIST_VISIBLE_POSITION.BOTTOM:
                        {
                            scrollView.scrollLeft = cellDom.offsetLeft
                                + cellDom.clientWidth - scrollView.clientWidth;
                            break;
                        }
                    }
                } else {
                    switch (position) {
                        case LIST_VISIBLE_POSITION.TOP:
                        {
                            scrollView.scrollTop = cellDom.offsetTop - this.nativeHeaderHeight;
                            break;
                        }
                        case LIST_VISIBLE_POSITION.CENTER:
                        {
                            scrollView.scrollTop = cellDom.offsetTop
                                + ((cellDom.clientHeight - scrollView.clientHeight) / 2);
                            break;
                        }
                        case LIST_VISIBLE_POSITION.BOTTOM:
                        {
                            scrollView.scrollTop = cellDom.offsetTop
                                + cellDom.clientHeight - scrollView.clientHeight;
                            break;
                        }
                    }
                }
            }
            this.handleSlideListScroll();
        });
    };

    /**
     * 获取滚动的offset
     * @returns
     */
    public getScrollOffset = () => ({ offsetX: 0, offsetY: 0 });

    private renderFrameAndCheckDisplay = () => {
        this.handleCellDisplayState();
    };

    // 根据原生顶部栏是否展示获取顶部预留高度，防止滚动到指定页时被遮挡
    private get nativeHeaderHeight(): number {
        return this.getAppServices().viewableServiceLayer.nativeTitleBarManager?.visible
            ? (MOBILE_HEAD_HEIGHT + this.topBannerHeight) : 0;
    }

    // 编辑态时 topBanner 的高度应为 0
    private get topBannerHeight(): number {
        return !this.props.editMode
            ? (this.getAppServices().viewableServiceLayer.topBannerService?.getTopBannerHeight() || 0) : 0;
    }


    private registerScrollSlideIntoViewByIndexMobile = () => {
        window.addEventListener('scrollSlideIntoViewByIndexMobile', this.scrollSlideIntoView);
    };

    private scrollSlideIntoView = (event: CustomEvent) => {
        this.scrollSlideIntoViewByIndex(event.detail.slideIndex);
    };

    /** （平滑）滚动到指定 slideIndex */
    private scrollSlideIntoViewByIndex = (index: number) => {
        const listContainer = this.getListContainer();
        const slideDomElement = listContainer?.children?.[index];
        if (!slideDomElement) return;

        slideDomElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
        });
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private handleSlideListScroll = throttle(() => {
        this.props.onScroll?.(this);
        this.handleCellDisplayState();
    }, 20);

    private getListContainer = (): HTMLDivElement => this.listContainer!;

    private handleCellDisplayState = () => {
        const visibleCellIDs: string[] = [];
        const listContainer = this.getListContainer();
        const scrollView = this.container;
        if (listContainer && scrollView) {
            const cells = listContainer.childNodes;
            if (cells && cells.length > 0) {
                const listRect = scrollView.getBoundingClientRect();  // 这里要获取scrollView的rect
                this.clientHeight && (listRect.height = this.clientHeight);
                // listRect 如果有scale，scrollTop会受到scale后的影响
                const preCellShow = false;  // 上一个cell是否处于show状态
                const topOffset = this.fixedScrollTop || 0;
                this.handleDisplayForCells(
                    cells, listRect,
                    0, topOffset, preCellShow,
                    visibleCellIDs,
                );
            }
        }
        this.visibleCellIDs = visibleCellIDs;
        this.props.onVisibleCellsChange?.(this.visibleCellIDs);
    };

    private handleDisplayForCells(
        cells: NodeList, listRect: DOMRect, leftOffset: number,
        topOffset: number, preCellShow: boolean, visibleCellIDs: string[],
    ) {
        for (let i = 0; i < cells.length; i++) {
            const cellID = this.cellIDs[i];
            const cell = cells[i] as HTMLElement;
            const cellRect = cell.getBoundingClientRect();
            if (this.isOuterScreen(cellRect, leftOffset, listRect, topOffset)
            ) {
                // // 非可视区域
                // if (preCellShow) break;  // 优化一下，如果上一个显示，这个不显示，直接可以停止检查了
                // lasse 不能在此break，否则导致向上滚动时无法触发onCellDisappearAtIndex
                const indexInVisibleArray = this.visibleCellIDs.indexOf(cellID);
                if (indexInVisibleArray >= 0) {
                    this.props.onCellDisappearAtIndex?.(cell, i);
                }
            } else {
                // 可视区域
                // eslint-disable-next-line no-param-reassign
                preCellShow = true;
                visibleCellIDs.push(cellID);
                const indexInVisibleArray = this.visibleCellIDs.indexOf(cellID);
                if (indexInVisibleArray < 0) {
                    this.props.onCellAppearAtIndex?.(cell, i);
                }
            }
        }
    }

    private isOuterScreen(cellRect: DOMRect, leftOffset: number, listRect: DOMRect, topOffset: number): boolean {
        return cellRect.left - leftOffset >= listRect.right
            || cellRect.right - leftOffset <= listRect.left
            || cellRect.top - topOffset >= listRect.bottom
            || cellRect.bottom - topOffset <= listRect.top + this.nativeHeaderHeight;
    }

    /**
     * 列表是否竖屏展示，true是，否则不是
     * @returns {boolean}
     */
    private isVertical = (): boolean => this.props.layout === List.Layout.VERTICAL;

    private getPaddingTop = (vertical: boolean) => (isSupportNativeTitleBar() && !browser.isPad && vertical && this.props.editMode ? `${MOBILE_NATIVE_JUDGE_HEIGHT - MOBILE_HEAD_HEIGHT}px` : 0);

    private renderChildren(): React.ReactNode[] {
        const children: React.ReactNode[] = [];
        const cellIDs: string[] = [];
        const cellCount = (this.props.cellCountForList?.(this)) || 0;
        if (cellCount > 0) {
            for (let i = 0; i < cellCount; i++) {
                const cell = this.props.cellForListAtIndex?.(i);
                children.push(cell);

                const cellID = this.props.cellIDForListAtIndex?.(i);
                cellIDs.push(cellID as string);
            }
        }
        this.cellIDs = cellIDs;
        return children;
    }

    private getAppServices = () => AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices;
}
